import React, { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import useLeftHandedness from 'components/ClassroomsModule/hooks/use-left-handness';
import GET_WDT_STUDENT_SETTINGS from 'graph/settings/wdt/get-wdt-student-settings-query';
import UPDATE_WDT_STUDENT_LETTER_NUMBER_ORDER_SETTING from 'graph/settings/wdt/update-wdt-student-letter-number-order-setting-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import { Student, WdtSettingLeftHandednessData } from 'types/schema-generated-types';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';

interface LeftHandednessInputs {
  label: string;
  value: boolean;
}

interface LeftHandednessProps {
  student: Student;
  setting: WdtSettingLeftHandednessData | null;
  expanded: string | null;
  handleChangeExpanded: (panel: string | null) => () => void;
}

const LeftHandOptions: LeftHandednessInputs[] = [
  {
    label: 'Left-to-right cross stroke (Default)',
    value: false
  },
  {
    label: 'Right-to-left cross stroke (Recommended)',
    value: true
  }
];

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    }
  };
});

const getStudentWdtSettingsMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_WDT_STUDENT_SETTINGS,
      variables: {
        where: {
          studentId
        }
      },
      reducer: (
        cachedData,
        { updateWdtStudentSettingLeftHandedness }
      ): { wdtStudentSettings: any } => {
        const [{ value: leftHandedness }] = updateWdtStudentSettingLeftHandedness;
        return {
          wdtStudentSettings: {
            ...cachedData?.wdtStudentSettings,
            leftHandedness
          }
        };
      }
    }
  ])
});

const LeftHandedness: FC<LeftHandednessProps> = ({
  student,
  setting,
  expanded,
  handleChangeExpanded
}) => {
  const { t } = useTranslation('LeftHandOptionSetting');
  const classes = useStyles();

  const studentId = +student.id;
  const [, { openAlert }] = useStore();
  const [updateWdtStudentSettingLeftHandedness] = useMutation(
    UPDATE_WDT_STUDENT_LETTER_NUMBER_ORDER_SETTING,
    getStudentWdtSettingsMutationCacheUpdate(studentId)
  );

  const { leftHandedness, toggleLeftHanded, toggleRtlCrossStroke } = useLeftHandedness(setting);

  const handleChange = (): void => toggleRtlCrossStroke();

  const handleOnCancel = (): void => {
    toggleLeftHanded(setting?.leftHanded || false);
    toggleRtlCrossStroke(setting?.rtlCrossStroke || false);
  };

  const handleOnConfirm = async (): Promise<void> => {
    try {
      await updateWdtStudentSettingLeftHandedness({
        variables: {
          data: {
            studentId,
            value: leftHandedness
          }
        }
      });
      openAlert(getSettingSuccessAlert('Left-Hand Option', t));
    } catch (error) {
      //TODO
      console.log('Error');
    }
  };

  return (
    <SettingsCard
      title={t('Left-Hand Option')}
      description={t('Enable Left-Hand Option.')}
      expanded={expanded}
      panel="leftHandOption"
      handleChangeExpanded={handleChangeExpanded}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        <FormControlLabel
          control={
            <Switch
              checked={leftHandedness.leftHanded}
              onChange={toggleLeftHanded}
              name="leftHandedness"
              color="primary"
            />
          }
          label={
            leftHandedness.leftHanded ? 'Left-Hand Option Enabled' : 'Left-Hand Option Disabled'
          }
        />
        {leftHandedness.leftHanded && (
          <RadioGroup
            aria-label="left hand setting"
            name="leftHandSetting"
            value={leftHandedness.rtlCrossStroke}
            onChange={handleChange}
          >
            {LeftHandOptions.map(
              ({ label, value }, key): ReactElement => (
                <FormControlLabel
                  key={`left-hand-option-${key}`}
                  value={value}
                  control={<Radio />}
                  label={label}
                />
              )
            )}
          </RadioGroup>
        )}
      </FormControl>
    </SettingsCard>
  );
};

export default LeftHandedness;
